/**
 * Main-thread client: create LP worker, post solve message, return result.
 * LP solving runs in the worker so the UI is not blocked.
 */

import type { LpSolveFailureDetail, SolveInput, SolveResult } from './tripLpSolver';

function bannedEdgesToSerializable(input: SolveInput['bannedEdgesByItemType']): Record<string, string[]> | undefined {
    if (!input || Object.keys(input).length === 0) return undefined;
    const out: Record<string, string[]> = {};
    for (const [k, set] of Object.entries(input)) {
        if (set && set.size > 0) out[k] = Array.from(set);
    }
    return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Solve the trip LP in a web worker. Returns the same shape as solveTripLp.
 */
export function solveTripLpInWorker(input: SolveInput): Promise<SolveResult> {
    return new Promise((resolve, reject) => {
        let worker: Worker;
        try {
            worker = new Worker(new URL('../workers/tripLpWorker.ts', import.meta.url), { type: 'module' });
        } catch (e) {
            reject(e);
            return;
        }
        const onMessage = (
            ev: MessageEvent<
                | { type: 'result'; solutions: Record<string, { A: unknown[]; T: unknown[] }> }
                | { type: 'error'; message: string; itemTypeId?: string; solverStatus?: string }
            >
        ) => {
            worker.removeEventListener('message', onMessage);
            worker.removeEventListener('error', onError);
            worker.terminate();
            const d = ev.data;
            if (d.type === 'result') {
                resolve({ ok: true, solutions: d.solutions } as SolveResult);
            } else {
                const failure: LpSolveFailureDetail | undefined =
                    d.itemTypeId != null
                        ? {
                              itemTypeId: d.itemTypeId,
                              solverStatus: d.solverStatus ?? 'error',
                              message: d.message,
                          }
                        : undefined;
                resolve({
                    ok: false,
                    message:
                        d.solverStatus === 'Infeasible' && d.itemTypeId ? `No feasible flow for item type (${d.itemTypeId})` : d.message,
                    failure,
                });
            }
        };
        const onError = (ev: ErrorEvent) => {
            worker.removeEventListener('message', onMessage);
            worker.removeEventListener('error', onError);
            worker.terminate();
            reject(ev.error ?? new Error('Worker error'));
        };
        worker.addEventListener('message', onMessage);
        worker.addEventListener('error', onError);
        worker.postMessage({
            type: 'solve',
            nodes: input.nodes,
            edges: input.edges,
            requirementsByItemType: input.requirementsByItemType,
            consumableByItemType: input.consumableByItemType,
            bannedEdgesByItemType: bannedEdgesToSerializable(input.bannedEdgesByItemType),
        });
    });
}
